import { useEffect, useRef, useState } from 'react';
import { Calculator, Search, AlertTriangle, X, RotateCcw, Pill } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { searchDrugs, getDrugDetails, getDrugPedsDosing, type DrugSearchResult, type DrugDetails, type DrugMolecule, type PedsDosing } from '@/lib/api';
import { cn } from '@/lib/utils';

const FREQUENCIES = [
  { value: 1, label: 'Once daily (OD)' },
  { value: 2, label: 'Twice daily (BD)' },
  { value: 3, label: 'Three times daily (TDS)' },
  { value: 4, label: 'Four times daily (QID)' },
];

const FORM_FILTERS = [
  { label: 'All',       key: 'all',       match: [] as string[] },
  { label: 'Syrup',     key: 'syrup',     match: ['syrup', 'suspension', 'solution', 'drops', 'linctus', 'elixir', 'mixture', 'liquid', 'oral liquid'] },
  { label: 'Tablet',    key: 'tablet',    match: ['tablet', 'tab', 'dispersible', 'chewable'] },
  { label: 'Capsule',   key: 'capsule',   match: ['capsule', 'cap', 'soft gelatin'] },
  { label: 'Injection', key: 'injection', match: ['injection', 'infusion', 'vial', 'ampoule'] },
];

const TO_MG: Record<string, number> = { mg: 1, mcg: 0.001, g: 1000 };
const PRACTICAL_TABLET_FRACTIONS = [0.25, 0.5, 0.75, 1];

const WEIGHT_FOR_AGE = [
  { age: 'Newborn',   kg: '3–4'  },
  { age: '3 months',  kg: '5–7'  },
  { age: '6 months',  kg: '6–9'  },
  { age: '1 year',    kg: '8–12' },
  { age: '2 years',   kg: '10–14'},
  { age: '4 years',   kg: '14–18'},
  { age: '6 years',   kg: '18–24'},
  { age: '8 years',   kg: '22–30'},
  { age: '10 years',  kg: '28–38'},
  { age: '12 years',  kg: '35–50'},
];

function formatNumber(n: number): string {
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(2);
}

function formatMass(mg: number): string {
  if (mg > 0 && mg < 1) return `${formatNumber(mg * 1000)} mcg`;
  return `${formatNumber(mg)} mg`;
}

function drugSubtitle(d: { composition_raw: string | null; form: string }): string {
  return [d.composition_raw, d.form].filter(Boolean).join(' · ');
}

function moleculeLabel(m: DrugMolecule): string {
  return m.strength ? `${m.molecule_name} — ${m.strength}` : m.molecule_name;
}

function isPracticalTabletFraction(units: number): boolean {
  if (units <= 0) return false;
  const frac = units - Math.floor(units);
  // frac === 0 means a whole number of tablets (1, 2, 3…) — always measurable
  if (frac < 0.02) return true;
  return PRACTICAL_TABLET_FRACTIONS.some(f => Math.abs(frac - f) < 0.02);
}

// Convert an internal mg value to the molecule's native unit for display,
// so breakdown formulas stay unit-consistent (e.g. "250 mcg ÷ 250 mcg/5ml").
function formatInUnit(mg: number, unit: string): string {
  const factor = TO_MG[unit];
  if (!factor) return `${formatNumber(mg)} mg`;
  return `${formatNumber(mg / factor)} ${unit}`;
}

function ResultValue({ label, value, hasInput }: { label: string; value: string | null; hasInput: boolean }) {
  return (
      <div className="flex-1 text-center">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
        <p className={cn(
            "text-3xl font-bold tabular-nums tracking-tight transition-colors",
            hasInput && value ? "text-foreground" : "text-muted-foreground/30"
        )}>
          {hasInput && value ? value : "—"}
        </p>
      </div>
  );
}

export default function PedsCalculatorPage() {
  const { currentUser } = useAuth();
  const { hasFeature } = usePermission();

  const [weight, setWeight]               = useState('');
  const [doseRate, setDoseRate]           = useState('');
  const [doseRateUnit, setDoseRateUnit]   = useState<'mg' | 'mcg'>('mg');
  const [doseRateBasis, setDoseRateBasis] = useState<'day' | 'dose'>('day');
  const [frequency, setFrequency]         = useState(2);

  const [formFilter, setFormFilter] = useState<string>('all');
  const [pedsDosing, setPedsDosing]             = useState<PedsDosing[]>([]);
  const [loadingPedsDosing, setLoadingPedsDosing] = useState(false);

  // Stale-response guard: only the latest request wins
  const pedsRequestRef = useRef(0);

  const [query, setQuery]                         = useState('');
  const [results, setResults]                     = useState<DrugSearchResult[]>([]);
  const [searching, setSearching]                 = useState(false);
  const [showResults, setShowResults]             = useState(false);
  const [selectedDrug, setSelectedDrug]           = useState<DrugDetails | null>(null);
  const [selectedMoleculeId, setSelectedMoleculeId] = useState<number | null>(null);
  // FIX 3: track whether molecule was auto-selected (no is_primary found)
  const [moleculeAutoSelected, setMoleculeAutoSelected] = useState(false);
  const [loadingDrug, setLoadingDrug]             = useState(false);

  const debounceRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  // FIX 1: replace onBlur+setTimeout with a ref-based outside-click handler
  const searchWrapRef = useRef<HTMLDivElement>(null);

  // ── Drug search debounce ──────────────────────────────────────────────────

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 3) { setResults([]); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      searchDrugs(query, currentUser?.doctor_id || currentUser?.id || '')
          .then(setResults)
          .catch(() => setResults([]))
          .finally(() => setSearching(false));
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, currentUser?.doctor_id, currentUser?.id]);

  // FIX 1: close dropdown on outside click — reliable on both mouse and touch
  useEffect(() => {
    function handleOutsideClick(e: MouseEvent | TouchEvent) {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, []);

  // ── Drug selection ────────────────────────────────────────────────────────

  const fetchPedsDosing = async (drugId: number, moleculeId: number, wt: number | null) => {
    const requestId = ++pedsRequestRef.current;
    setLoadingPedsDosing(true);
    try {
      const rows = await getDrugPedsDosing(drugId, moleculeId, wt ?? undefined);
      if (requestId !== pedsRequestRef.current) return; // stale — discard
      setPedsDosing(rows);
    } catch {
      if (requestId !== pedsRequestRef.current) return;
      setPedsDosing([]);
    } finally {
      if (requestId === pedsRequestRef.current) setLoadingPedsDosing(false);
    }
  };

  const handleSelectDrug = async (drug: DrugSearchResult) => {
    setShowResults(false);
    setQuery(drug.brand_name);
    setPedsDosing([]);
    setLoadingDrug(true);
    try {
      const details = await getDrugDetails(drug.id);
      if (!details) {
        // API returned null (network error or drug not found) — reset query so the
        // search field doesn't appear stuck showing a name with nothing selected.
        setQuery('');
        return;
      }
      setSelectedDrug(details);
      const molecules = details?.molecules ?? [];
      const primary   = molecules.find(m => m.is_primary);
      const autoSelected = !primary && molecules.length > 1;
      setMoleculeAutoSelected(autoSelected);
      const resolvedMoleculeId = primary?.molecule_id ?? molecules[0]?.molecule_id ?? null;
      setSelectedMoleculeId(resolvedMoleculeId);
      // Only auto-fetch peds dosing when the molecule is unambiguous (single molecule,
      // or a primary is explicitly marked). When autoSelected=true the doctor must
      // pick the correct ingredient first — showing dosing for the wrong molecule is unsafe.
      const shouldAutoFetch = resolvedMoleculeId && !autoSelected;
      if (shouldAutoFetch) {
        const wt = parseFloat(weight);
        await fetchPedsDosing(drug.id, resolvedMoleculeId, wt > 0 ? wt : null);
      }
    } finally {
      setLoadingDrug(false);
    }
  };

  const clearDrug = () => {
    pedsRequestRef.current++;        // cancel any in-flight request
    setSelectedDrug(null);
    setSelectedMoleculeId(null);
    setMoleculeAutoSelected(false);
    setPedsDosing([]);
    setLoadingPedsDosing(false);
    setQuery('');
    setResults([]);
  };

  // Re-fetch peds dosing when weight changes — debounced so we don't fire on every keystroke.
  // Intentionally omits selectedDrug/selectedMoleculeId from deps: those are handled by
  // handleSelectDrug (immediate) and the molecule Select onValueChange (below).
  useEffect(() => {
    if (!selectedDrug || !selectedMoleculeId) return;
    const timer = setTimeout(() => {
      const wt = parseFloat(weight);
      fetchPedsDosing(selectedDrug.id, selectedMoleculeId, wt > 0 ? wt : null);
    }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weight]);

  const resetAll = () => {
    setWeight('');
    setDoseRate('');
    setDoseRateUnit('mg');
    setDoseRateBasis('day');
    setFrequency(2);
    setFormFilter('all');
    clearDrug();
  };

  if (!hasFeature('peds_calculator')) {
    return (
        <div className="flex flex-col items-center justify-center h-full text-center px-4 py-20">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
            <Calculator className="w-6 h-6 text-muted-foreground" />
          </div>
          <h2 className="text-base font-semibold mb-1">Pediatric Calculator Not Available</h2>
          <p className="text-sm text-muted-foreground max-w-xs">
            Not included in your current plan. Contact support to upgrade.
          </p>
        </div>
    );
  }

  // Apply a peds dosing suggestion: auto-fill dose rate, unit, basis, frequency
  const applyPedsSuggestion = (row: PedsDosing) => {
    // Parse MySQL DECIMAL which arrives as a string like "10.0000" — strip trailing zeros
    setDoseRate(formatNumber(Number(row.dose_per_kg)));
    setDoseRateUnit(row.dose_unit === 'mcg' ? 'mcg' : 'mg');
    setDoseRateBasis(row.dose_basis === 'per_dose' ? 'dose' : 'day');
    if (row.doses_per_day >= 1 && row.doses_per_day <= 4) {
      setFrequency(row.doses_per_day);
    } else if (row.doses_per_day > 4) {
      // Frequency dropdown only supports up to QID (4×/day) — leave frequency unchanged
      // and warn the doctor to set it manually.
      // We still apply dose rate / basis since those are correct.
      console.warn(`PedsCalculator: doses_per_day=${row.doses_per_day} exceeds dropdown range — frequency not auto-filled`);
    }
  };

  // ── Calculations ──────────────────────────────────────────────────────────

  const weightKg    = parseFloat(weight);
  const rate        = parseFloat(doseRate);
  const validInputs = weightKg > 0 && rate > 0;

  const molecules        = selectedDrug?.molecules ?? [];
  const selectedMolecule = molecules.find(m => m.molecule_id === selectedMoleculeId);

  // Derive the correct unit label from the drug form for solid results and breakdown display.
  const solidFormLabel = (() => {
    const f = (selectedDrug?.form ?? '').toLowerCase();
    if (f.includes('capsule'))  return 'capsule(s)';
    if (f.includes('sachet'))   return 'sachet(s)';
    if (f.includes('chewable')) return 'chewable tablet(s)';
    if (f.includes('tablet') || f.includes('tab')) return 'tablet(s)';
    return 'unit(s)';
  })();

  // Detect when the drug's declared form is liquid but the DB has it classified as solid —
  // in this case any "units per dose" result is meaningless and dangerous.
  const LIQUID_FORM_KEYWORDS = ['syrup', 'suspension', 'solution', 'drops', 'elixir', 'linctus', 'mixture', 'liquid', 'emulsion', 'oral liquid'];
  const drugFormIsLiquid = LIQUID_FORM_KEYWORDS.some(k => (selectedDrug?.form ?? '').toLowerCase().includes(k));

  let perDoseMg: number | null         = null;
  let dailyMg: number | null           = null;
  let formulationResult: string | null = null;
  let formulationNote: string | null   = null;
  const notes: string[] = [];

  // FIX 3: surface auto-selection warning early, before dose entry
  if (moleculeAutoSelected && selectedDrug) {
    notes.push(
        `Ingredient auto-selected (${selectedMolecule?.molecule_name ?? 'first listed'}) — no primary ingredient marked for this drug. Confirm this is the one your dose rate refers to.`
    );
  }

  if (weightKg > 0) {
    if (weightKg > 40)
      notes.push('Weight is in the adolescent/adult range — confirm mg/kg dosing still applies; many drugs switch to fixed adult dosing above ~40 kg.');
    else if (weightKg < 1)
      notes.push(`Weight entered is ${weightKg} kg — confirm this is not in grams. If correct (e.g. premature neonate), use neonatal-specific dosing guidance rather than standard pediatric mg/kg rates.`);
    else if (weightKg < 2)
      notes.push(`Weight is ${weightKg} kg — confirm this is in kilograms, not grams. A term newborn is typically 3–4 kg.`);
  }

  // Active peds dosing row for the selected molecule (in-range preferred)
  const activePedsRow = pedsDosing.find(r => r.in_range) ?? pedsDosing[0] ?? null;

  let maxDoseCapped = false;

  if (validInputs) {
    const totalMg = weightKg * rate * TO_MG[doseRateUnit];
    if (doseRateBasis === 'day') {
      dailyMg   = totalMg;
      perDoseMg = totalMg / frequency;
    } else {
      perDoseMg = totalMg;
      dailyMg   = totalMg * frequency;
    }

    // Max dose capping from peds dosing table
    if (activePedsRow) {
      // MySQL DECIMAL arrives as string from mysql2 — always parse
      const maxSingle = activePedsRow.max_single_dose != null ? Number(activePedsRow.max_single_dose) : null;
      const maxDaily  = activePedsRow.max_daily_dose  != null ? Number(activePedsRow.max_daily_dose)  : null;
      const outOfRange = !activePedsRow.in_range;

      // Check single-dose cap first
      if (maxSingle !== null && perDoseMg! > maxSingle) {
        const suffix = outOfRange ? ' (reference from out-of-range entry — verify against current guidelines)' : '';
        notes.push(`Per-dose amount (${formatMass(perDoseMg!)}) exceeds the maximum single dose of ${formatMass(maxSingle)} — capped at ${formatMass(maxSingle)}.${suffix}`);
        perDoseMg = maxSingle;
        dailyMg   = maxSingle * frequency;
        maxDoseCapped = true;
      }

      // Check daily cap independently — even after a single-dose cap, daily may still exceed
      if (maxDaily !== null && dailyMg! > maxDaily) {
        const suffix = outOfRange ? ' (reference from out-of-range entry — verify against current guidelines)' : '';
        notes.push(`Daily total (${formatMass(dailyMg!)}) exceeds the maximum daily dose of ${formatMass(maxDaily)} — capped at ${formatMass(maxDaily)}.${suffix}`);
        dailyMg   = maxDaily;
        perDoseMg = maxDaily / frequency;
        maxDoseCapped = true;
      }
    }

    if (!maxDoseCapped && dailyMg! > 3000)
      notes.push('Total daily dose exceeds 3000 mg — double-check the weight (kg, not lb), dose rate, and whether the rate is per day or per dose.');

    if (selectedDrug) {
      if (!molecules.length) {
        formulationNote = 'No composition data available for this drug';
      } else if (!selectedMolecule) {
        formulationNote = 'Select which ingredient the dose rate above refers to';
      } else if (!selectedMolecule.is_strength_parsed || !selectedMolecule.dose_value || !selectedMolecule.dose_unit) {
        formulationNote = 'Strength data not available for this ingredient';
      } else {
        const factor = TO_MG[selectedMolecule.dose_unit];
        if (!factor) {
          formulationNote = `Cannot auto-convert — unsupported unit "${selectedMolecule.dose_unit}"`;
        } else {
          // Warn when dose rate unit (mcg) is much smaller than the molecule's unit (mg),
          // producing a near-zero result — almost always a unit entry mistake.
          if (doseRateUnit === 'mcg' && selectedMolecule.dose_unit === 'mg') {
            notes.push(
              `Possible unit mismatch: dose rate is in mcg/kg but ${selectedMolecule.molecule_name} is typically dosed in mg/kg. The calculated dose (${formatMass(perDoseMg!)}) may be 1000× lower than intended — verify the dose rate unit.`
            );
          }

          const perDoseInUnit = perDoseMg! / factor;
          if (selectedMolecule.strength_type === 'liquid' && selectedMolecule.per_volume_value && selectedMolecule.per_volume_unit) {
            const ml = (perDoseInUnit / selectedMolecule.dose_value) * selectedMolecule.per_volume_value;
            if (ml < 0.01) {
              formulationResult = null;
              formulationNote = `Result is effectively zero (${formatNumber(ml)} ${selectedMolecule.per_volume_unit}) — dose is too small for this formulation. Verify the dose rate and unit, or select a more concentrated formulation.`;
            } else {
              formulationResult = `${formatNumber(ml)} ${selectedMolecule.per_volume_unit} per dose`;
              if (ml < 0.5)
                formulationNote = 'Volume is very small — hard to measure accurately with a standard oral syringe; consider a more dilute formulation';
              else if (ml > 20)
                formulationNote = `Volume is large (${formatNumber(ml)} ${selectedMolecule.per_volume_unit} per dose) — verify the dose rate basis (per day vs. per dose) and the drug concentration selected`;
            }
          } else if (selectedMolecule.strength_type === 'solid') {
            // If the drug's declared form is a liquid (syrup, suspension, etc.) but the DB
            // classifies it as solid, the volume cannot be computed — showing "X units" is
            // dangerous because a clinician cannot administer "units" of a liquid.
            if (drugFormIsLiquid) {
              formulationResult = null;
              formulationNote = `"${selectedDrug.form}" is a liquid formulation but is stored as a solid in the drug database — volume in ml cannot be calculated automatically. Use the strength (${selectedMolecule.strength ?? selectedMolecule.dose_value + ' ' + selectedMolecule.dose_unit}) to calculate the volume manually: dose ÷ concentration × volume per unit.`;
            } else {
              const units = perDoseInUnit / selectedMolecule.dose_value;
              if (units < 0.01) {
                formulationResult = null;
                formulationNote = `Result is effectively zero (${formatNumber(units)} ${solidFormLabel}) — dose is too small for this formulation. Verify the dose rate and unit, or select a liquid formulation.`;
              } else {
                formulationResult = `${formatNumber(units)} ${solidFormLabel} per dose`;
                if (!isPracticalTabletFraction(units))
                  formulationNote = 'Not an easily measurable fraction — consider a liquid formulation or different strength';
              }
            }
          } else {
            formulationNote = 'Dose-to-volume conversion not supported for this formulation';
          }
        }
      }
      if (formulationNote) notes.push(formulationNote);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const hasError = notes.some(n =>
    n.includes('zero') || n.includes('mismatch') || n.includes('1000×') ||
    n.includes('liquid form') || n.includes('exceeds the maximum')
  ) || (validInputs && !!selectedDrug && !!selectedMolecule && selectedMolecule.strength_type === 'solid' && drugFormIsLiquid);

  // Parse formulation result into value + unit for the hero display
  const formulationParts = formulationResult
    ? formulationResult.match(/^([\d.]+)\s+(.+?)\s+per dose$/)
    : null;
  const formulationValue = formulationParts?.[1] ?? null;
  const formulationUnit  = formulationParts?.[2] ?? null;

  const isErrorNote = (n: string) =>
    n.includes('zero') || n.includes('mismatch') || n.includes('1000×') || n.includes('liquid form') || n.includes('liquid formulation but is stored') || n.includes('exceeds the maximum');

  const activeFilter = FORM_FILTERS.find(f => f.key === formFilter)!;
  const filteredResults = formFilter === 'all'
    ? results
    : results.filter(d => activeFilter.match.some(m => d.form.toLowerCase().includes(m)));

  // Calculation log row — keeps the log JSX compact
  const CalcRow = ({ label, value, accent = false, faded = false, truncate = false }: {
    label: string; value: string; accent?: boolean; faded?: boolean; truncate?: boolean;
  }) => (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn(
        truncate ? "truncate max-w-[120px] text-right" : "",
        accent ? "font-bold text-primary" : faded ? "text-muted-foreground" : "font-medium text-foreground"
      )}>{value}</span>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
            <Calculator className="w-4 h-4 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-foreground">Pediatric Dose Calculator</h1>
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Weight-based · mg/kg</p>
          </div>
        </div>
        <button onClick={resetAll} className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground px-2.5 py-1.5 rounded-lg border border-transparent hover:border-border hover:bg-muted/40 transition-all">
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_288px] gap-4 items-start">

        {/* ── Left: numbered steps ── */}
        <div className="space-y-3">

          {/* ① Drug */}
          <div className="relative bg-card border border-border rounded-2xl">
            <span className="absolute right-3 -top-1 text-[88px] font-black leading-none text-muted-foreground/[0.04] select-none pointer-events-none overflow-hidden" aria-hidden="true">1</span>
            <div className="relative p-5 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">Drug</span>
                <span className="text-[10px] text-muted-foreground/45">— optional, enables ml / tablet calculation</span>
              </div>

              {selectedDrug ? (
                <div className={cn(
                  "rounded-xl border-l-[3px] px-3 py-2.5 space-y-1.5 border",
                  drugFormIsLiquid && selectedMolecule?.strength_type === 'solid'
                    ? "border-l-destructive border-destructive/20 bg-destructive/5"
                    : "border-l-primary border-primary/15 bg-primary/5"
                )}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <p className="text-sm font-bold text-foreground truncate">{selectedDrug.brand_name}</p>
                      {selectedDrug.form && (
                        <span className={cn(
                          "text-[9px] font-extrabold px-1.5 py-0.5 rounded uppercase tracking-widest flex-shrink-0",
                          drugFormIsLiquid && selectedMolecule?.strength_type === 'solid'
                            ? "bg-destructive/15 text-destructive"
                            : "bg-primary/15 text-primary"
                        )}>
                          {selectedDrug.form}
                        </span>
                      )}
                    </div>
                    <button onClick={clearDrug} className="text-muted-foreground hover:text-foreground flex-shrink-0 p-1 rounded hover:bg-muted/50 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {selectedDrug.pack_size && <p className="text-[11px] text-muted-foreground">{selectedDrug.pack_size}</p>}
                  {molecules.length === 0 && <p className="text-xs text-muted-foreground">{drugSubtitle(selectedDrug) || 'No composition data'}</p>}
                  {molecules.length === 1 && <p className="text-[11px] font-mono text-muted-foreground bg-muted/40 rounded-lg px-2 py-1">{moleculeLabel(molecules[0])}</p>}
                  {molecules.length > 1 && (
                    <div className="space-y-1">
                      <p className="text-[10px] text-muted-foreground">Which ingredient does the dose rate refer to?</p>
                      <Select value={selectedMoleculeId !== null ? String(selectedMoleculeId) : ''} onValueChange={v => {
                        const mid = Number(v);
                        setSelectedMoleculeId(mid);
                        setMoleculeAutoSelected(false);
                        if (selectedDrug) {
                          const wt = parseFloat(weight);
                          fetchPedsDosing(selectedDrug.id, mid, wt > 0 ? wt : null);
                        }
                      }}>
                        <SelectTrigger className="h-7 text-xs rounded-lg"><SelectValue placeholder="Select ingredient…" /></SelectTrigger>
                        <SelectContent>
                          {molecules.map(m => <SelectItem key={m.molecule_id} value={String(m.molecule_id)}>{moleculeLabel(m)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Peds dosing suggestions — hidden until molecule is explicitly chosen */}
                  {!moleculeAutoSelected && (loadingPedsDosing || pedsDosing.length > 0) && (
                    <div className="space-y-1.5 pt-0.5">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Standard dose</p>
                      {/* Spinner only on first load (no chips yet) — weight-change refetches are silent */}
                      {loadingPedsDosing && pedsDosing.length === 0 && (
                        <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-border/50 bg-muted/20">
                          <div className="w-3 h-3 rounded-full border-2 border-primary/30 border-t-primary animate-spin flex-shrink-0" />
                          <span className="text-[11px] text-muted-foreground">Loading standard dose…</span>
                        </div>
                      )}
                      {/* Chips stay visible during re-fetch; dim slightly to signal update */}
                      {pedsDosing.length > 0 && <div className={cn("flex flex-col gap-1.5 transition-opacity", loadingPedsDosing ? "opacity-50 pointer-events-none" : "opacity-100")}>
                        {pedsDosing.map(row => {
                          // MySQL DECIMAL(8,4) returns "10.0000" as string — normalise
                          const lo = formatNumber(Number(row.dose_per_kg));
                          const hi = row.dose_per_kg_max ? formatNumber(Number(row.dose_per_kg_max)) : null;
                          const rateLabel = hi ? `${lo}–${hi} ${row.dose_unit}/kg` : `${lo} ${row.dose_unit}/kg`;
                          const basisLabel = row.dose_basis === 'per_day' ? '/day' : '/dose';
                          const indicationLabel = row.indication
                            ? row.indication.split('/').map(s => s.trim() ? s[0].toUpperCase() + s.slice(1) : s).join(' / ')
                            : null;

                          // Build weight/age bounds string
                          const fmtAge = (m: number) => m < 12 ? `${m}m` : m % 12 === 0 ? `${m/12}y` : `${Math.floor(m/12)}y${m%12}m`;
                          const wBounds: string[] = [];
                          if (row.min_weight_kg != null) wBounds.push(`≥${row.min_weight_kg} kg`);
                          if (row.max_weight_kg != null) wBounds.push(`≤${row.max_weight_kg} kg`);
                          const aBounds: string[] = [];
                          if (row.min_age_months != null) aBounds.push(`≥${fmtAge(row.min_age_months)}`);
                          if (row.max_age_months != null) aBounds.push(`≤${fmtAge(row.max_age_months)}`);
                          const bounds = [...wBounds, ...aBounds].join(' · ');

                          return (
                            <button
                              key={row.id}
                              type="button"
                              onClick={() => applyPedsSuggestion(row)}
                              className={cn(
                                "w-full text-left px-2.5 py-2 rounded-lg border text-xs transition-colors",
                                row.in_range
                                  ? "border-primary/25 bg-primary/5 hover:bg-primary/10"
                                  : "border-border bg-muted/20 hover:bg-muted/40 opacity-60"
                              )}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-semibold text-foreground">
                                  {rateLabel}{basisLabel} · {row.frequency_label}
                                </span>
                                <span className="text-[9px] font-bold text-muted-foreground/60 uppercase tracking-wider shrink-0">{row.source}</span>
                              </div>
                              {/* Indication + bounds on the same line */}
                              {(indicationLabel || bounds) && (
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                  {[indicationLabel, bounds].filter(Boolean).join(' · ')}
                                </p>
                              )}
                              {/* Special notes — always shown; safety-critical */}
                              {row.special_notes && (
                                <p className="text-[10px] text-warning/80 mt-1 leading-relaxed">{row.special_notes}</p>
                              )}
                              {row.doses_per_day > 4 && (
                                <p className="text-[10px] text-warning font-medium mt-0.5">⚠ Frequency ({row.doses_per_day}×/day) not in dropdown — set manually after applying</p>
                              )}
                              {!row.in_range && (
                                <p className="text-[10px] text-warning font-medium mt-0.5">⚠ Outside validated weight range</p>
                              )}
                            </button>
                          );
                        })}
                      </div>}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2.5">
                  {/* Form filter chips */}
                  <div className="flex gap-1.5 flex-wrap">
                    {FORM_FILTERS.map(f => (
                      <button key={f.key} type="button" onClick={() => setFormFilter(f.key)}
                        className={cn(
                          "h-7 px-3 rounded-full text-[11px] font-semibold transition-all border",
                          formFilter === f.key
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-transparent text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                        )}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                  {/* Search */}
                  <div className="relative" ref={searchWrapRef}>
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <Input
                      className="pl-9 h-10 text-sm rounded-xl"
                      value={query}
                      onChange={e => { setQuery(e.target.value); setShowResults(true); }}
                      onFocus={() => setShowResults(true)}
                      placeholder={formFilter === 'all' ? 'Search by brand name…' : `Search ${activeFilter.label} by brand name…`}
                    />
                    {loadingDrug && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">Loading…</span>}
                    {showResults && query.trim().length >= 3 && (
                      <div className="absolute z-30 mt-1 w-full rounded-xl border border-border bg-popover shadow-lg max-h-64 overflow-y-auto">
                        {searching ? (
                          <p className="px-3 py-3 text-xs text-muted-foreground">Searching…</p>
                        ) : filteredResults.length > 0 ? (
                          filteredResults.map(d => (
                            <button key={d.id} type="button"
                              className="w-full text-left px-3 py-2.5 hover:bg-muted transition-colors border-b border-border/40 last:border-0 group"
                              onMouseDown={() => handleSelectDrug(d)}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium group-hover:text-primary transition-colors">{d.brand_name}</span>
                                {d.form && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase tracking-widest">{d.form}</span>}
                              </div>
                              <p className="text-xs text-muted-foreground truncate mt-0.5">{d.composition_raw ?? ''}</p>
                            </button>
                          ))
                        ) : results.length > 0 ? (
                          <div className="px-3 py-3 space-y-1.5">
                            <p className="text-xs text-muted-foreground">No {activeFilter.label} found for "{query}"</p>
                            <button type="button" className="text-xs text-primary font-medium hover:underline" onMouseDown={() => setFormFilter('all')}>
                              Show all forms ({results.length})
                            </button>
                          </div>
                        ) : (
                          <p className="px-3 py-3 text-xs text-muted-foreground">No drugs found</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ② Parameters */}
          <div className="relative bg-card border border-border rounded-2xl overflow-hidden">
            <span className="absolute right-3 -top-1 text-[88px] font-black leading-none text-muted-foreground/[0.04] select-none pointer-events-none" aria-hidden="true">2</span>
            <div className="relative p-5 space-y-3">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">Parameters</span>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold text-foreground">Weight <span className="font-normal text-muted-foreground">(kg)</span></Label>
                  <Input type="number" min="0" step="0.1" value={weight} onChange={e => setWeight(e.target.value)} placeholder="e.g. 12.5" className="h-9 text-sm font-mono rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold text-foreground">Frequency</Label>
                  <Select value={String(frequency)} onValueChange={v => setFrequency(Number(v))}>
                    <SelectTrigger className="h-9 text-sm rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FREQUENCIES.map(f => <SelectItem key={f.value} value={String(f.value)}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold text-foreground">Dose rate <span className="font-normal text-muted-foreground">(per kg)</span></Label>
                  <div className="flex gap-1.5">
                    <Input className="flex-1 h-9 text-sm font-mono rounded-xl" type="number" min="0" step="0.01" value={doseRate} onChange={e => setDoseRate(e.target.value)} placeholder="e.g. 40" />
                    <Select value={doseRateUnit} onValueChange={v => setDoseRateUnit(v as 'mg' | 'mcg')}>
                      <SelectTrigger className="w-[76px] h-9 text-xs rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mg">mg/kg</SelectItem>
                        <SelectItem value="mcg">mcg/kg</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold text-foreground">Rate is per</Label>
                  <div className="h-9 flex rounded-xl border border-border bg-muted/20 p-0.5 gap-0.5">
                    {([{ value: 'day', label: 'Day total' }, { value: 'dose', label: 'Per dose' }] as const).map(opt => (
                      <button key={opt.value} type="button" onClick={() => setDoseRateBasis(opt.value)}
                        className={cn('flex-1 rounded-lg text-xs font-semibold transition-all',
                          doseRateBasis === opt.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Warnings */}
          {notes.length > 0 && (
            <div className="space-y-2">
              {notes.map((note, i) => (
                <div key={i} className={cn(
                  "flex items-start gap-3 px-3.5 py-3 rounded-xl border text-xs leading-relaxed",
                  isErrorNote(note) ? "bg-destructive/5 border-destructive/20 text-destructive" : "bg-warning/5 border-warning/20 text-foreground"
                )}>
                  <AlertTriangle className={cn("w-3.5 h-3.5 flex-shrink-0 mt-0.5", isErrorNote(note) ? "text-destructive" : "text-warning")} />
                  <p>{note}</p>
                </div>
              ))}
            </div>
          )}

          <p className="text-[10px] text-muted-foreground/45 text-center pb-2">
            Reference only — verify against current pediatric dosing guidelines before prescribing.
          </p>
        </div>

        {/* ── Right: sticky result + reference ── */}
        <div className="space-y-3 lg:sticky lg:top-4 lg:self-start">

          {/* ── Result hero — changes state based on what's computed ── */}
          {formulationResult && formulationValue && formulationUnit ? (
            /* Valid formulation → vivid primary panel */
            <div className="rounded-2xl overflow-hidden bg-primary">
              <div className="px-5 pt-5 pb-4">
                <p className="text-[9px] font-black uppercase tracking-[0.25em] text-primary-foreground/50 mb-3">Give per dose</p>
                <div className="flex items-end gap-2 mb-3">
                  <span className="text-[56px] font-mono font-black tabular-nums leading-none text-primary-foreground">{formulationValue}</span>
                  <span className="text-2xl font-mono font-semibold text-primary-foreground/55 pb-1.5 leading-none">{formulationUnit}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Pill className="w-3 h-3 text-primary-foreground/35 flex-shrink-0" />
                  <span className="text-[11px] text-primary-foreground/45 truncate">
                    {selectedDrug?.brand_name}{selectedDrug?.form ? ` · ${selectedDrug.form}` : ''}{selectedMolecule?.strength ? ` · ${selectedMolecule.strength}` : ''}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 border-t border-primary-foreground/12">
                <div className="px-4 py-3 text-center border-r border-primary-foreground/12">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-primary-foreground/40 mb-1">Per dose</p>
                  <p className="text-lg font-mono font-bold text-primary-foreground tabular-nums">{perDoseMg !== null ? formatMass(perDoseMg) : '—'}</p>
                </div>
                <div className="px-4 py-3 text-center">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-primary-foreground/40 mb-1">Daily total</p>
                  <p className="text-lg font-mono font-bold text-primary-foreground/65 tabular-nums">{dailyMg !== null ? formatMass(dailyMg) : '—'}</p>
                </div>
              </div>
            </div>
          ) : (
            /* No valid formulation → neutral panel with mg values */
            <div className={cn(
              "rounded-2xl overflow-hidden border",
              hasError ? "border-destructive/30 bg-destructive/5" : "border-border bg-card"
            )}>
              <div className="grid grid-cols-2 divide-x divide-border">
                <div className="px-5 py-5 text-center">
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground mb-2">Per dose</p>
                  <p className={cn(
                    "text-[40px] font-mono font-black tabular-nums leading-none",
                    !validInputs ? "text-muted-foreground/10"
                    : hasError ? "text-muted-foreground/30"
                    : "text-primary"
                  )}>
                    {validInputs && perDoseMg !== null ? formatMass(perDoseMg) : "—"}
                  </p>
                </div>
                <div className="px-5 py-5 text-center">
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground mb-2">Daily total</p>
                  <p className={cn(
                    "text-[40px] font-mono font-black tabular-nums leading-none",
                    !validInputs ? "text-muted-foreground/10"
                    : hasError ? "text-muted-foreground/20"
                    : "text-foreground/70"
                  )}>
                    {validInputs && dailyMg !== null ? formatMass(dailyMg) : "—"}
                  </p>
                </div>
              </div>
              {/* Error/status strip — must never truncate; full text always readable */}
              <div className={cn(
                "border-t px-4 text-[11px] leading-snug",
                hasError && validInputs && selectedDrug ? "py-3" : "py-2 min-h-[30px] flex items-center",
                hasError ? "border-destructive/20" : "border-border"
              )}>
                {selectedDrug ? (
                  <span className={cn(hasError ? "text-destructive" : "text-muted-foreground/55")}>
                    {validInputs ? (formulationNote ?? 'Formulation cannot be calculated') : 'Enter parameters to see formulation'}
                  </span>
                ) : (
                  <span className="text-muted-foreground/30">Select a drug to see ml / tablet count</span>
                )}
              </div>
            </div>
          )}

          {/* Calculation log */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border bg-muted/30">
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground">Calculation</p>
            </div>
            {validInputs ? (
              <div className="p-4 font-mono text-xs space-y-1.5">
                <CalcRow label="weight" value={`${weightKg} kg`} />
                <CalcRow label="rate" value={`${doseRate} ${doseRateUnit}/kg/${doseRateBasis === 'day' ? 'day' : 'dose'}`} />
                <CalcRow label="freq" value={`${frequency}×/day`} />
                <div className="border-t border-dashed border-border/50 pt-1.5 space-y-1.5">
                  {doseRateBasis === 'day' ? (<>
                    <p className="text-muted-foreground text-[10px]">= {weightKg} × {doseRate} {doseRateUnit}/kg</p>
                    <CalcRow label="daily total" value={dailyMg !== null ? formatMass(dailyMg) : '—'} />
                    <CalcRow label={`÷${frequency} doses`} value={perDoseMg !== null ? formatMass(perDoseMg) : '—'} accent />
                  </>) : (<>
                    <p className="text-muted-foreground text-[10px]">= {weightKg} × {doseRate} {doseRateUnit}/kg</p>
                    <CalcRow label="per dose" value={perDoseMg !== null ? formatMass(perDoseMg) : '—'} accent />
                    <CalcRow label={`×${frequency} doses`} value={dailyMg !== null ? formatMass(dailyMg) : '—'} />
                  </>)}
                  {selectedDrug && selectedMolecule && perDoseMg !== null && (
                    <div className="border-t border-dashed border-border/50 pt-1.5 space-y-1.5">
                      <CalcRow label="drug" value={selectedDrug.brand_name} truncate />
                      {selectedDrug.form && <CalcRow label="form" value={selectedDrug.form} />}
                      <CalcRow label="strength" value={selectedMolecule.strength ?? '—'} />
                      {selectedMolecule.strength_type === 'liquid' && selectedMolecule.dose_value && selectedMolecule.per_volume_value && selectedMolecule.per_volume_unit && selectedMolecule.dose_unit && (
                        <p className="text-muted-foreground text-[10px] break-all">
                          {formatInUnit(perDoseMg, selectedMolecule.dose_unit)} ÷ {selectedMolecule.dose_value} {selectedMolecule.dose_unit} × {selectedMolecule.per_volume_value} {selectedMolecule.per_volume_unit}
                        </p>
                      )}
                      {selectedMolecule.strength_type === 'solid' && selectedMolecule.dose_value && selectedMolecule.dose_unit && !drugFormIsLiquid && (
                        <p className="text-muted-foreground text-[10px]">
                          {formatInUnit(perDoseMg, selectedMolecule.dose_unit)} ÷ {selectedMolecule.dose_value} {selectedMolecule.dose_unit}/{solidFormLabel.replace('(s)', '')}
                        </p>
                      )}
                      {selectedMolecule.strength_type === 'solid' && drugFormIsLiquid && (
                        <p className="text-[10px] text-destructive">⚠ liquid form / solid DB — ml cannot be auto-calculated</p>
                      )}
                      {formulationResult && (
                        <div className="border-t border-border/40 pt-1.5">
                          <CalcRow label="= give" value={formulationResult} accent />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="p-4 text-xs text-muted-foreground/30 font-mono">awaiting input…</p>
            )}
          </div>

          {/* Weight-for-age */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border bg-muted/30">
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground">Weight-for-Age</p>
              <p className="text-[10px] text-muted-foreground/45 mt-0.5">WHO reference · individual variation is normal</p>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border/40">
                  <th className="text-left text-[10px] font-bold text-muted-foreground uppercase tracking-wider px-4 py-2">Age</th>
                  <th className="text-right text-[10px] font-bold text-muted-foreground uppercase tracking-wider px-4 py-2">kg</th>
                </tr>
              </thead>
              <tbody>
                {WEIGHT_FOR_AGE.map(({ age, kg }) => {
                  const [lo, hi] = kg.split('–').map(Number);
                  const isMatch = weightKg > 0 && weightKg >= lo && weightKg <= hi;
                  return (
                    <tr key={age} className={cn("border-b border-border/20 last:border-0 transition-colors", isMatch ? "bg-primary/8" : "")}>
                      <td className={cn("px-4 py-1.5 text-xs", isMatch ? "font-bold text-foreground" : "text-muted-foreground")}>{age}</td>
                      <td className={cn("px-4 py-1.5 text-xs text-right font-mono", isMatch ? "font-bold text-primary" : "text-foreground")}>{kg}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>
  );
}